import { Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { trpc } from '@/lib/trpc';
import { formatCurrency, formatDate } from '@/lib/utils';
import { downloadXlsx, dateStamp } from '@/lib/download-xlsx';
import { useSessionState } from '@/hooks/use-session-state';
import { ReportExportButton } from './report-export-button';

/** Konaklama vergisi (travel): taxable accommodation turnover, tax collected, tie-out to 360.06. */
export function AccommodationTaxReport() {
  const today = new Date();
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const [startDate, setStartDate] = useSessionState('travel:accounting-reports:accommodation:startDate', startOfMonth.toISOString().split('T')[0] ?? '');
  const [endDate, setEndDate] = useSessionState('travel:accounting-reports:accommodation:endDate', today.toISOString().split('T')[0] ?? '');
  const { data, isLoading, error } = trpc.financialReports.getAccommodationTaxReport.useQuery({
    startDate: new Date(startDate || startOfMonth),
    endDate: new Date(endDate || today),
  });

  return (
    <div className="space-y-4">
      <div className="bg-card flex flex-wrap items-end gap-4 rounded-lg border p-4 print:hidden">
        <div className="space-y-2">
          <Label>Başlangıç / From</Label>
          <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-44" />
        </div>
        <div className="space-y-2">
          <Label>Bitiş / To</Label>
          <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-44" />
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
        </div>
      ) : error ? (
        <div className="border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">{error.message}</div>
      ) : data ? (
        <>
          <ReportExportButton
            disabled={data.invoices.length === 0}
            onExport={() =>
              downloadXlsx(
                [
                  ...data.invoices.map((i) => ({ Fatura: i.invoiceNumber, Tarih: formatDate(i.issueDate), 'Para birimi': i.currency, Matrah: i.base, 'Konaklama vergisi': i.tax })),
                  { Fatura: 'TOPLAM', Tarih: '', 'Para birimi': '', Matrah: data.summary.taxableBase, 'Konaklama vergisi': data.summary.totalTax },
                ],
                'Konaklama Vergisi',
                `konaklama-vergisi-${startDate}-${endDate}-${dateStamp()}.xlsx`
              )
            }
          />
          <div className="grid gap-3 md:grid-cols-5">
            <Card label="Matrah / Taxable base" value={formatCurrency(data.summary.taxableBase)} />
            <Card label="Hesaplanan vergi / Tax" value={formatCurrency(data.summary.totalTax)} />
            <Card label="Fatura / Invoices" value={String(data.summary.invoiceCount)} />
            <Card label="360.06 GL" value={formatCurrency(data.summary.glCollected)} />
            <Card label="Fark / Variance" value={formatCurrency(data.summary.glVariance)} tone={Math.abs(data.summary.glVariance) < 0.01 ? 'ok' : 'warn'} />
          </div>
          <div className="bg-card overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-2 font-medium">Fatura</th>
                  <th className="px-4 py-2 font-medium">Tarih</th>
                  <th className="px-4 py-2 font-medium">Para birimi</th>
                  <th className="px-4 py-2 text-right font-medium">Matrah</th>
                  <th className="px-4 py-2 text-right font-medium">Vergi</th>
                </tr>
              </thead>
              <tbody>
                {data.invoices.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-3 text-sm text-muted-foreground">
                      Bu dönemde konaklama vergili fatura yok.
                    </td>
                  </tr>
                )}
                {data.invoices.map((i) => (
                  <tr key={i.invoiceNumber} className="border-b last:border-b-0">
                    <td className="px-4 py-2 font-mono text-xs">{i.invoiceNumber}</td>
                    <td className="px-4 py-2">{formatDate(i.issueDate)}</td>
                    <td className="px-4 py-2 font-mono text-xs">{i.currency}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{formatCurrency(i.base, i.currency)}</td>
                    <td className="px-4 py-2 text-right tabular-nums font-medium">{formatCurrency(i.tax, i.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </div>
  );
}

function Card({ label, value, tone = 'neutral' }: { label: string; value: string; tone?: 'neutral' | 'ok' | 'warn' }) {
  const cls = tone === 'ok' ? 'border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-900/20' : tone === 'warn' ? 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20' : 'bg-card';
  return (
    <div className={`rounded-lg border p-4 ${cls}`}>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-xl font-bold tabular-nums">{value}</p>
    </div>
  );
}
